import React, { RefObject, Suspense, useRef } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { FormikProvider, useFormik } from "formik";
import CenteredSpinner from "@/shared/components/centered-spinner";
import PathPickMembershipExpiredPanel from "@/features/pathpick/components/PathPickMembershipExpiredPanel";
import useMembership from "@/shared/hooks/useMembership";
import PathPickBasketHeader from "@/features/pathpick/features/basket/components/PathPickBasketHeader";
import PathPickBasketActionPanel from "@/features/pathpick/features/basket/components/action-panel";
import PathPickBasketListSection from "@/features/pathpick/features/basket/components/list";
import PathPickBasketNoResult from "@/features/pathpick/features/basket/components/PathPickBasketNoResult";
import useGetPathPickCounts from "@/features/pathpick/hooks/query/useGetPathPickCounts";
import { PathPickBasketSyncFlowProvider } from "@/features/pathpick/features/basket/contexts/PathPickBasketSyncFlowContext";
import { PathPickBasketFormValues } from "@/features/pathpick/features/basket/types";
import { Helmet } from "react-helmet-async";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

// 선택 상태/주문 모달 payload 등 교차 클라이언트 상태를 Formik으로 감싸 컨텍스트처럼 공유한다.
//
// useFormik을 이 래퍼에 두는 이유: children을 바깥에서 만들어 넘기면 엘리먼트 identity가 유지되므로,
// 선택 상태가 바뀔 때 React가 children 서브트리를 건너뛰고 컨텍스트 구독자만 리렌더한다.
// (페이지 컴포넌트에서 직접 useFormik을 호출하면 매 렌더마다 자식 JSX가 새로 만들어져 전체가 리렌더된다.)
// 검증 로직(validate/validationSchema)이 없으므로 값 변경 시 검증 파이프라인도 끈다 —
// 켜두면 setFieldValue 한 번에 SET_ISVALIDATING/SET_ERRORS 디스패치가 붙어 커밋이 2회로 늘어난다.
function PathPickBasketFormProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const formik = useFormik<PathPickBasketFormValues>({
    initialValues: {
      selectedProducts: new Map(),
      orderModalItems: null,
    },
    onSubmit: () => {},
    validateOnChange: false,
    validateOnBlur: false,
  });

  // 신청 플로우 상태는 별도 Provider가 소유한다 — 관문 해결 후 이어서 진행하려면
  // 한 핸들러에서 단계를 연쇄 전이해야 하는데, 배치되는 setFieldValue로는 안전하지 않다.
  // (children을 바깥에서 받는 형태라 여기서도 서브트리 리렌더 없이 컨텍스트 구독자만 깨어난다)
  return (
    <FormikProvider value={formik}>
      <PathPickBasketSyncFlowProvider>
        {children}
      </PathPickBasketSyncFlowProvider>
    </FormikProvider>
  );
}

function PathPickBasketPageContent({
  scrollContainerRef,
}: {
  // 목록 하단 "맨 위로" 버튼이 직접 올릴 스크롤 Box — 여기서는 전달만 한다.
  scrollContainerRef: RefObject<HTMLDivElement>;
}) {
  // 담은 상품이 하나도 없는지는 탭/검색 필터와 무관한 카운트 API로 판정한다.
  // 목록 조회 totalCount로 판정하면 디폴트 탭(작업 전)이 0건일 때 빈 화면이 잘못 뜬다.
  // (헤더가 이미 쓰는 캐시라 추가 요청이 없다)
  const { pathPickCounts, isPending } = useGetPathPickCounts();

  // 비suspense 쿼리라 초기 로드는 직접 스피너로 처리한다(빈 상태 오탐 방지).
  if (isPending) {
    return <CenteredSpinner />;
  }

  return (
    <PathPickBasketFormProvider>
      {pathPickCounts?.basket.totalCount === 0 ? (
        <PathPickBasketNoResult />
      ) : (
        <Flex flexDirection={"column"} gap={"16px"}>
          <PathPickBasketHeader />
          <PathPickBasketActionPanel />
          <PathPickBasketListSection
            scrollContainerRef={scrollContainerRef}
          />
        </Flex>
      )}
    </PathPickBasketFormProvider>
  );
}

function PathPickBasketPage() {
  const { tTitle } = useI18nHelper();
  const pageTitle = `${tTitle("common:navigation.path_pick")} | ${tTitle(
    "common:navigation.path_pick_basket"
  )}`;
  const { expiredStatus } = useMembership();
  // 이 Box가 이 화면의 유일한 스크롤 주체다(body는 스크롤되지 않는다).
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        padding: "16px",
        overflow: "auto",
        height: "100%",
        // 하단 패딩을 두면 목록 페이지네이션의 sticky bottom 기준선이 그만큼 올라가
        // 바가 화면 바닥에서 뜬다. 카드 바닥과 화면 바닥을 일치시켜 붙여 둔다.
        paddingBottom: 0,
      }}
    >
      <Helmet>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} />
      </Helmet>
      {/* 체험·멤버십 만료 시 헤더만 남기고 본문을 잠금 안내로 대체한다(시안). */}
      {expiredStatus ? (
        <Flex flexDirection={"column"} gap={"16px"} height={"100%"}>
          <PathPickBasketHeader />
          <PathPickMembershipExpiredPanel status={expiredStatus} />
        </Flex>
      ) : (
        <>
          {/* 하위 suspense 쿼리(usage/downloads)의 경계 — 라우트 상위에 Suspense가 없다. */}
          <Suspense fallback={<CenteredSpinner />}>
            <PathPickBasketPageContent
              scrollContainerRef={scrollContainerRef}
            />
          </Suspense>
        </>
      )}
    </Box>
  );
}

export default PathPickBasketPage;
